import {
    Button,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Paper
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import React, { useCallback, useMemo } from "react";
import {
    AddOutlined,
    CancelOutlined,
    CheckCircleOutlined,
    Delete,
    MoreHoriz
} from "@mui/icons-material";
import GridContainer from "components/common/GridContainer";
import GridItem from "components/common/GridItem";
import DGridAg from "components/common/DGridAg";
import useAgGrid from "components/common/DGridAg/hook/useAgGrid";
import useIsMobile from "utils/helpers/mediaQueryHelper";

function RowActionsMenu({ data, index, onEdit, onDelete }) {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <IconButton size="small" onClick={handleOpen}>
                <MoreHoriz fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem
                    onClick={() => {
                        onEdit(data, index);
                        handleClose();
                    }}
                >
                    <EditIcon fontSize="small" />
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDelete(data, index);
                        handleClose();
                    }}
                >
                    <DeleteIcon fontSize="small" />
                </MenuItem>
            </Menu>
        </>
    );
}

export default function EmployeeCollectionTable({
    resource,
    rows,
    onEdit,
    onDelete,
    onDeleteSelected,
    onAdd
}) {
    const [selectable, setSelectable] = React.useState(false);
    const [selectedRows, setSelectedRows] = React.useState([]);

    const isMobile = useIsMobile();

    const { dateFormatter } = useAgGrid();

    const rowSelection = useMemo(
        () =>
            selectable
                ? { mode: "multiRow", checkboxes: true, headerCheckbox: true }
                : undefined,
        [selectable]
    );

    const handleSelectionChanged = useCallback(
        (event) => {
            const selected = event.api.getSelectedRows();
            if (selected?.length) {
                setSelectedRows(
                    selected.map((row) => ({
                        id: row.id,
                        rowId: row.rowId
                    }))
                );
            } else {
                setSelectedRows([]);
            }
            // c?.(selectedRows);
        },
        [selectedRows]
    );

    const cellRenderer = ({ data, ...rest }) => {
        //console.log({ data, rest });
        const index = rest?.node?.sourceRowIndex;
        if (isMobile) {
            if (isMobile) {
                return (
                    <RowActionsMenu
                        data={data}
                        index={index}
                        onEdit={onEdit}
                        onDelete={onDelete}
                    />
                );
            }
        }
        return (
            <div style={{ display: "flex", gap: "8px" }}>
                <IconButton size="small" onClick={() => onEdit(data, index)}>
                    <EditIcon fontSize="small" />
                </IconButton>

                <IconButton
                    size="small"
                    color="error"
                    onClick={() => onDelete(data, index)}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </div>
        );
    };

    const mobileColDefs = isMobile
        ? {
              pinned: "left",
              lockPinned: true
          }
        : {};

    const colDefs = [
        ...resource.columns.map((column) => ({
            field: column.field,
            editable: !!column.editable,
            headerName: column.header,
            width: column.width,
            // flex: 1,
            wrapHeaderText: true,
            autoHeaderHeight: true,
            ...mobileColDefs,
            // headerClass: "centered-header",
            ...(column.width && !isMobile
                ? { width: column.width, flex: 0, textAlign: "center" }
                : { flex: 1, textAlign: "center" }),
            valueFormatter: column.type === "date" ? dateFormatter : undefined
        })),
        {
            headerName: isMobile ? "..." : "Actions",
            field: "actions",
            pinned: "right",
            lockPinned: true,
            width: isMobile ? 40 : 100,
            flex: 0,
            cellRenderer
        }
    ];

    const headerActions = () => {
        return (
            <div style={{ padding: "8px 8px" }}>
                <GridContainer noWrap>
                    <GridItem>
                        <Button
                            variant="contained"
                            onClick={() => {
                                if (selectable) {
                                    setSelectedRows(null);
                                }
                                setSelectable((current) => !current);
                            }}
                            sx={{ padding: "0px 8px" }}
                        >
                            {selectable && (
                                <>
                                    <CancelOutlined
                                        sx={{
                                            fontSize: "14px",
                                            marginRight: "2px"
                                        }}
                                    />{" "}
                                    Cancel
                                </>
                            )}
                            {!selectable && (
                                <>
                                    <CheckCircleOutlined
                                        sx={{
                                            fontSize: "14px",
                                            marginRight: "2px"
                                        }}
                                    />{" "}
                                    Select
                                </>
                            )}
                        </Button>
                    </GridItem>
                    {selectable && selectedRows?.length > 0 && (
                        <GridItem>
                            <Button
                                variant="outlined"
                                sx={{
                                    // width: "26px",
                                    // height: "26px",
                                    minWidth: "unset",
                                    //borderRadius: "50%",
                                    padding: "0px 8px"
                                }}
                                onClick={() => onDeleteSelected(selectedRows)}
                            >
                                <DeleteIcon
                                    color="error"
                                    sx={{
                                        fontSize: "18px",
                                        marginRight: "2px",
                                        fontColor: "white"
                                        /// width: "24px",
                                        // height: "24px"
                                    }}
                                />{" "}
                                {selectedRows?.length}
                                {/*Delete*/}
                            </Button>
                        </GridItem>
                    )}
                    <GridItem>
                        <Button
                            variant="contained"
                            sx={{ padding: "0px 8px" }}
                            onClick={onAdd}
                        >
                            <AddOutlined
                                sx={{
                                    fontSize: "14px",
                                    marginRight: "2px"
                                }}
                            />{" "}
                            Add {!isMobile && resource.singular}
                        </Button>
                    </GridItem>
                </GridContainer>
            </div>
        );
    };

    return (
        <DGridAg
            rowData={rows}
            columnDefs={colDefs}
            singleClickEdit={true}
            domLayout="autoHeight"
            enableClickSelection={true}
            suppressCellFocus={true}
            getRowId={(params) => params.data.rowId || params.data.id}
            rowSelection={rowSelection}
            onSelectionChanged={selectable ? handleSelectionChanged : undefined}
            header={headerActions()}
        />
    );
}
